// Espelho (frontend) do registro de submódulos do CanalPro.
// Backend: backend/canalpro/submodules.py (fonte da verdade do gating).
// Aqui só precisamos REPLICAR as regras de default para EXIBIR no drawer o valor
// efetivo de cada submódulo quando o tenant ainda não tem override explícito —
// sem materializar esses defaults no banco ao salvar (ver dirty-tracking no drawer).

export const CANALPRO_SUBMODULES = [
  'import',
  'publish',
  'leads',
  'chat',
  'metrics',
  'monitor',
  'diagnostics',
] as const

export type CanalproSubmodule = (typeof CANALPRO_SUBMODULES)[number]

// Editáveis no Business Center vs DERIVADOS do modo (read-only no drawer).
// Espelha backend/canalpro/submodules.py (EDITABLE_SUBMODULES / DERIVED_SUBMODULES).
export const EDITABLE_SUBMODULES = ['chat', 'metrics', 'monitor', 'diagnostics'] as const
export const DERIVED_SUBMODULES = ['import', 'publish', 'leads'] as const
export type EditableSubmodule = (typeof EDITABLE_SUBMODULES)[number]

export const SUBMODULE_LABELS: Record<CanalproSubmodule, string> = {
  import: 'Importação de imóveis',
  publish: 'Publicação / Refresh',
  leads: 'Leads (API GraphQL)',
  chat: 'Chat CanalPro',
  metrics: 'Métricas',
  monitor: 'Monitor de token',
  diagnostics: 'Saúde dos anúncios',
}

export function submoduleCapabilityKey(name: CanalproSubmodule): string {
  return `canalpro_${name}_enabled`
}

type Caps = Record<string, any> | null | undefined

/**
 * Default do submódulo quando NÃO há override explícito. Espelha DEFAULT_RULES do
 * backend: chat/diagnostics = OFF; leads deriva do modo de leads;
 * publish/metrics/import = (imoveis_mode === graphql); monitor = canalpro_enabled.
 */
export function submoduleDefault(name: CanalproSubmodule, caps: Caps): boolean {
  const c = caps || {}
  const imoveisGraphql = c.canalpro_imoveis_mode === 'graphql'
  switch (name) {
    case 'chat':
    case 'diagnostics':
      return false
    case 'leads':
      return c.canalpro_leads_mode === 'graphql'
    case 'publish':
    case 'metrics':
    case 'import':
      return imoveisGraphql
    case 'monitor':
      return !!c.canalpro_enabled
  }
}

/** True se o tenant tem override explícito (boolean) para o submódulo. */
export function hasExplicitOverride(name: CanalproSubmodule, caps: Caps): boolean {
  const c = caps || {}
  return typeof c[submoduleCapabilityKey(name)] === 'boolean'
}

/**
 * Valor efetivo. Derivados (import/publish/leads) IGNORAM override (fonte = modo);
 * só os editáveis (chat/metrics/monitor) respeitam override explícito.
 * Espelha is_submodule_enabled do backend.
 */
export function effectiveSubmodule(name: CanalproSubmodule, caps: Caps): boolean {
  const c = caps || {}
  if (name === 'diagnostics' && (!c.canalpro_enabled || c.canalpro_imoveis_mode !== 'graphql')) return false
  if ((EDITABLE_SUBMODULES as readonly string[]).includes(name)) {
    const explicit = c[submoduleCapabilityKey(name)]
    if (typeof explicit === 'boolean') return explicit
  }
  return submoduleDefault(name, caps)
}

export type ImoveisMode = 'xml' | 'graphql'
export type LeadsMode = 'webhook' | 'graphql'

export const DEFAULT_IMOVEIS_MODE: ImoveisMode = 'xml'
export const DEFAULT_LEADS_MODE: LeadsMode = 'webhook'

export interface CanalproDraft {
  imoveisMode: ImoveisMode
  leadsMode: LeadsMode
  leadsPollInterval?: number | null
  subs: Record<EditableSubmodule, boolean>
}

/**
 * Estado inicial do drawer. Modes com default; `subs` só dos EDITÁVEIS.
 * Diagnostics conserva o toggle configurado ao trocar temporariamente para XML;
 * o runtime continua bloqueado nesse modo.
 */
export function readCanalproDraft(caps: Caps): CanalproDraft {
  const c = caps || {}
  const subs = {} as Record<EditableSubmodule, boolean>
  for (const name of EDITABLE_SUBMODULES) subs[name] = effectiveSubmodule(name, c)
  if (typeof c.canalpro_diagnostics_enabled === 'boolean') {
    subs.diagnostics = c.canalpro_diagnostics_enabled
  }
  return {
    imoveisMode: (c.canalpro_imoveis_mode ?? DEFAULT_IMOVEIS_MODE) as ImoveisMode,
    leadsMode: (c.canalpro_leads_mode ?? DEFAULT_LEADS_MODE) as LeadsMode,
    leadsPollInterval: c.canalpro_leads_poll_interval_minutes ?? null,
    subs,
  }
}

export interface CanalproSavePayload {
  modes: {
    canalpro_imoveis_mode?: ImoveisMode
    canalpro_leads_mode?: LeadsMode
    canalpro_leads_poll_interval_minutes?: number
  }
  submodules: Record<string, boolean>
}

/**
 * Payload de salvamento: SÓ o que mudou entre `initial` e `current`.
 * - modes: só os modes alterados (não materializa o default se não foi tocado).
 * - submodules: só os submódulos alterados (evita congelar defaults herdados no JSON).
 * A exclusividade dos modes é garantida pelo modelo (cada mode é valor único).
 */
export function buildCanalproSavePayload(
  initial: CanalproDraft,
  current: CanalproDraft,
): CanalproSavePayload {
  const modes: CanalproSavePayload['modes'] = {}
  if (current.imoveisMode !== initial.imoveisMode) modes.canalpro_imoveis_mode = current.imoveisMode
  if (current.leadsMode !== initial.leadsMode) modes.canalpro_leads_mode = current.leadsMode
  if (
    current.leadsMode === 'graphql' &&
    current.leadsPollInterval !== initial.leadsPollInterval &&
    current.leadsPollInterval != null
  ) {
    modes.canalpro_leads_poll_interval_minutes = current.leadsPollInterval
  }

  const submodules: Record<string, boolean> = {}
  for (const name of EDITABLE_SUBMODULES) {
    if (current.subs[name] !== initial.subs[name]) {
      submodules[submoduleCapabilityKey(name)] = current.subs[name]
    }
  }
  return { modes, submodules }
}
